import type {
  BetActivityItem,
  BetActivityData,
  DailyBetTask,
  BetTaskItem,
  ReleaseRuleItem,
} from "./BetActivity.types";
import {
  preciseMultiply,
  getBetActivityEndTimeFromCache,
  getDoubleBetEndTimeFromCache,
  getTomorrowEndTimeFromCache,
} from "./BetActivity.utils";

/**
 * 检查是否有可领取的奖励（canClaim = true && claimed = false）
 */
export const hasClaimableRewards = (
  dailyBetTask: DailyBetTask | undefined | null,
  onlyDay0: boolean = false
): boolean => {
  if (!dailyBetTask?.betData) return false;

  const { taskList, currentBetAmount = 0 } = dailyBetTask.betData;
  if (!taskList?.length) return false;

  return taskList.some(task => {
    if (task.betAmount > currentBetAmount) return false;
    return task.releaseRuleItem?.some(rule => {
      if (onlyDay0 && rule.day !== 0) return false;
      return rule.canClaim === true && rule.claimed !== true;
    });
  });
};

/**
 * 检查是否应该显示 Bet 活动
 * 条件：dailyBetTask 存在 && enabled === true && countdownSeconds > 0
 */
export const shouldShowBetActivity = (
  dailyBetTask: DailyBetTask | undefined | null
): boolean => {
  if (!dailyBetTask) return false;
  if (dailyBetTask.enabled !== true) return false;
  if (hasClaimableRewards(dailyBetTask, true)) return true;
  if (
    !dailyBetTask.betData?.countdownSeconds ||
    dailyBetTask.betData?.countdownSeconds <= 0
  )
    return false;
  return true;
};

/**
 * 检查当天是否已点击过 bet activity 图标
 */
export const hasBetActivityClickedToday = (
  userId?: string,
  dailyBetTaskId?: string
): boolean => {
  if (!userId) return false;
  const today = new Date().toDateString();
  const key = `bet-activity-clicked:${userId}:${dailyBetTaskId}:${today}`;
  return localStorage.getItem(key) === "true";
};

/**
 * 标记当天已点击过 bet activity 图标
 */
export const markBetActivityClickedToday = (
  userId?: string,
  dailyBetTaskId?: string
): void => {
  if (!userId) return;
  const today = new Date().toDateString();
  const key = `bet-activity-clicked:${userId}:${dailyBetTaskId}:${today}`;
  localStorage.setItem(key, "true");
};

/**
 * 获取 day0 可领取奖励的数量
 * @param dailyBetTask 任务数据
 * @returns day0 可领取奖励的数量
 */
export const getDay0ClaimableCount = (
  dailyBetTask: DailyBetTask | undefined | null
): number => {
  if (!dailyBetTask?.betData?.taskList) return 0;

  const { taskList, currentBetAmount = 0 } = dailyBetTask.betData;
  let count = 0;

  taskList.forEach(task => {
    if (task.betAmount <= currentBetAmount) {
      const hasDay0Claimable = task.releaseRuleItem?.some(
        rule =>
          rule.day === 0 && rule.canClaim === true && rule.claimed !== true
      );
      if (hasDay0Claimable) {
        count++;
      }
    }
  });

  return count;
};

/**
 * 检查是否应该显示感叹号
 * 条件：有可领取奖励（day0 或 day1）或 当天未点击过
 */
export const shouldShowBetActivityBadge = (
  dailyBetTask: DailyBetTask | undefined | null,
  userId?: string
): boolean => {
  if (!dailyBetTask) return false;

  if (hasClaimableRewards(dailyBetTask)) {
    return true;
  }

  if (!hasBetActivityClickedToday(userId, dailyBetTask?.id ?? "")) {
    return true;
  }

  return false;
};

/**
 * 将真实 betData 转换为 BetActivityData 格式
 * @param dailyBetTask 原始数据
 */
export const convertBetDataToActivityData = (
  dailyBetTask: DailyBetTask | undefined
): BetActivityData => {
  const betData = dailyBetTask?.betData;
  const taskList = betData?.taskList || [];
  const currentBetAmount = betData?.currentBetAmount || 0;

  const isDoubleBet = betData?.balanceWarning === true;
  const doubleMultiplier = betData?.betMultiplier || 1;

  const items: BetActivityItem[] = taskList.map(
    (task: BetTaskItem, index: number) => {
      const releaseRules = task.releaseRuleItem || [];
      const day0Rule = releaseRules.find((r: ReleaseRuleItem) => r.day === 0);
      const day1Rule = releaseRules.find(
        (r: ReleaseRuleItem) => r.day === 1 && r.rate > 0
      );

      const isBatchClaim = !!(day0Rule && day1Rule);

      const day0Claimed = day0Rule?.claimed === true;
      const day0CanClaim = day0Rule?.canClaim === true;
      const day1Claimed = day1Rule?.claimed === true;
      const day1CanClaim = day1Rule?.canClaim === true;

      let bonus: number;
      let tomorrowBonus: number | undefined;

      if (isBatchClaim) {
        if (day0Claimed) {
          bonus = preciseMultiply(task.rewardAmount, day1Rule?.rate || 0);
          tomorrowBonus = undefined;
        } else {
          bonus = preciseMultiply(task.rewardAmount, day0Rule?.rate || 0);
          tomorrowBonus = preciseMultiply(
            task.rewardAmount,
            day1Rule?.rate || 0
          );
        }
      } else {
        bonus = task.rewardAmount;
        tomorrowBonus = undefined;
      }

      let status: BetActivityItem["status"] = "locked";

      if (currentBetAmount < task.betAmount) {
        status = "locked";
      } else if (isBatchClaim) {
        if (day0Claimed && day1Claimed) {
          status = "claimed";
        } else if (day0Claimed) {
          status = day1CanClaim ? "available" : "tomorrow";
        } else {
          status = day0CanClaim ? "available" : "locked";
        }
      } else {
        if (day0Claimed) {
          status = "claimed";
        } else if (day0CanClaim) {
          status = "available";
        }
      }

      return {
        id: String(index + 1),
        betAmount: task.betAmount,
        bonus,
        tomorrowBonus,
        status,
        isBatchClaim,
        releaseRuleItem: releaseRules,
        originalRewardAmount: task.rewardAmount,
      };
    }
  );

  const betActivityEndTime = getBetActivityEndTimeFromCache();
  const doubleBetEndTime = getDoubleBetEndTimeFromCache();
  const tomorrowEndTime = getTomorrowEndTimeFromCache();

  return {
    currentBetAmount,
    items,
    endTime: betActivityEndTime,
    isDoubleBet,
    doubleMultiplier,
    doubleBetEndTime,
    tomorrowEndTime,
    validPeriod: dailyBetTask?.validPeriod,
    isAllTasksCompleted: items.every(item => item.status === "claimed"),
  };
};

/**
 * ✅ 判断金额文本是否太长（超过8个字符）
 * @param currSymbol 货币符号
 * @param amount 金额数字
 * @param prefix 前缀（如 "+"）
 * @returns 是否为长金额
 */
export const isLongAmount = (
  currSymbol: string,
  amount: string | number,
  prefix: string = ""
): boolean => {
  const amountStr = typeof amount === "number" ? String(amount) : amount;
  const fullText = `${prefix}${currSymbol}${amountStr}`;
  return fullText.length > 8;
};
